import {ChangeEvent, useEffect, useRef, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {FormattedMessage, useIntl, defineMessages, MessageDescriptor} from 'react-intl'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner,
  Card,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import {useDisclourse} from '../../hooks/useDisclourse'
//import {AsideMenucommissionReports} from './components/AsideMenucommissionReports'
import {useLazyQuery, useQuery, useMutation} from '@apollo/client'
import {GET_ALL_COMMISSION_REPORTS_DETAILS_API} from '../../gql/queries/commissionReportsDetailsApiQueries'
import {GET_COMMISSION_REPORT_BY_ID_API} from '../../gql/queries/commissionReportsApiQueries'
import {UPDATE_COMMISSION_REPORT_DETAIL} from '../../gql/mutations/commissionReportDetailMutations'
import {toast} from 'react-toastify'
import {
  IResCommissionReportsDetailsAPI,
  NodeCommissionReportDetail,
  IResCreateCommissionReportDetail,
} from '../../types/commissionReportDetailApi'
import {hasExactPermission, hasPermission, parseId, parsePrice} from '../../helpers'
import moment from 'moment'
import {BadgeStatus} from './components/BadgeStatus'
import {ALL_PERMISSIONS} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalEditCommissionReport} from '../commissionReports/components/ModalEditCommissionReport'
import {NodeCommissionReport} from '../../types/commissionReportApi'
import {useUploadFile} from '../../hooks/useUploadFile'
import {UPLOAD_PAYMENT_COMMISSION_REPORT} from '../../gql/mutations/commissionReportMutations'
import {IResUploadPaymentCommissionReport} from '../../types/commissionReportApi'
import {ModalEditCommissionReportDetail} from './components/ModalEditCommissionReportDetail'
import {ModalAssignAgent} from './components/ModalAssignAgent'
import {ModalDeleteCommissionReportDetail} from './components/ModalDeleteCommissionReportDetail'
import {ModalAddCommissionReportDetail} from './components/ModaAddCommissionReportDetail'
import {
  TOGGLE_STATUS_COMMISSION_REPORT,
  CALCULATE_DEDUCTIONS_COMMISSION_REPORT,
} from '../../gql/mutations/commissionReportMutations'
import {ModalConfirmation} from '../commissionReports/components/ModalConfirmation'
import {ModalNotesClaims} from './components/ModalNotesClaims'
import {useGetUser} from '../../store/userStore'
import {OffcanvasSearchCommissionDetails} from './components/OffcanvasSearchCommissionDetails'
import {GET_ALL_USERS_FILTER, GET_ASSIGNMENT_USERS_LIST} from '../../gql/queries/usersQuery'
import {IResAllUsers} from '../oneUser/oneUserTypes'
import {URL} from '../../gql/client'
import {MultiSelectWithActions} from '../../components/MultiSelectWithActions'

const COMMISSION_TYPE_MESSAGES: Record<string, MessageDescriptor> = defineMessages({
  COMMISSION: {id: 'commissions.detail.commissionType.commission', defaultMessage: 'Commission'},
  DEDUCTION: {id: 'commissions.detail.commissionType.deduction', defaultMessage: 'Deduction'},
  CHARGEBACK: {id: 'commissions.detail.commissionType.chargeback', defaultMessage: 'Chargeback'},
  BONUS: {id: 'commissions.detail.commissionType.bonus', defaultMessage: 'Bonus'},
})

export const CommissionReportsDetailsPage = () => {
  const intl = useIntl()
  const emDash = '—'
  const space = ' '
  const commissionTypeLabel = (t: string) =>
    COMMISSION_TYPE_MESSAGES[t] ? intl.formatMessage(COMMISSION_TYPE_MESSAGES[t]) : t
  const permissions = useGetUser((s) => s.user?.permissions)
  const user = useGetUser((s) => s.user)
  const canEditCommission =
    permissions && hasExactPermission(permissions, ALL_PERMISSIONS.EDIT_COMMISSION)
  const canUseCommissionsFinder =
    permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMISSION_FINDER)
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const isAgente = userGroups.includes('Agente')
  // --- React Router
  const {id, client} = useParams<{id: string; client?: string}>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // --- Estado para el ID y nombre del reporte
  const [reportId, setReportId] = useState<string | null>(null)

  // --- GraphQL Hooks
  const [getOneCommissionReport, {data: commissionReportById}] = useLazyQuery(
    GET_COMMISSION_REPORT_BY_ID_API,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [
    gqlGetCommissionReportsDetails,
    {data: dataAllCommissionReportsDetails, loading, error, refetch: refetchEntries},
  ] = useLazyQuery<IResCommissionReportsDetailsAPI>(GET_ALL_COMMISSION_REPORTS_DETAILS_API, {
    fetchPolicy: 'no-cache',
  })

  const [gqlUpdate] =
    useMutation<IResCreateCommissionReportDetail>(UPDATE_COMMISSION_REPORT_DETAIL)

  const {data: dataMembers} = useQuery<IResAllUsers>(GET_ALL_USERS_FILTER, {
    fetchPolicy: 'no-cache',
    variables: {requireNpn: true},
  })

  // Lista de usuarios para el filtro Assigned To (igual que ModalFilters de Policies)
  const {data: dataAssignedToUsers} = useQuery<{allUsersList: {id: string; firstName: string; lastName: string; email: string; npn?: string | null}[]}>(
    GET_ASSIGNMENT_USERS_LIST,
    {fetchPolicy: 'no-cache', variables: {assignmentCandidates: true}}
  )

  // --- Modales y UI
  const {isOpen, onClose, onOpen} = useDisclourse()
  const [isOpenEdit, setIsOpenEdit] = useState(false)
  const [infoEdit, setInfoEdit] = useState<NodeCommissionReport | null>(null)
  const [isOpenEditDetail, setIsOpenEditDetail] = useState(false)
  const [infoEditDetail, setInfoEditDetail] = useState<NodeCommissionReportDetail | null>(null)
  const [isOpenAssignAgent, setIsOpenAssignAgent] = useState(false)
  const [isOpenNotesClaims, setIsOpenNotesClaims] = useState(false)

  // -- Reopen and close commission report for toggling status
  const [gqlToggleStatusCommissionReport] = useMutation(TOGGLE_STATUS_COMMISSION_REPORT)
  const [isOpenToggleModal, setIsOpenToggleModal] = useState(false)
  const [toggleAction, setToggleAction] = useState<'close' | 'reopen'>('close')

  const handleToggleStatus = async () => {
    if (!report?.id) return

    try {
      await gqlToggleStatusCommissionReport({
        variables: {
          id: parseId(report.id),
          newStatus: report.status === 'DRAFT' ? 'COMPLETED' : 'DRAFT',
        },
      })
      toast.success(
        report.status === 'DRAFT'
          ? intl.formatMessage({id: 'commissions.reports.toast.closed', defaultMessage: 'Commission Report closed successfully'})
          : intl.formatMessage({id: 'commissions.reports.toast.reopened', defaultMessage: 'Commission Report reopened successfully'})
      )
      await refetchReport()
      await refetchEntries()
    } catch (error: any) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    } finally {
      setIsOpenToggleModal(false)
    }
  }

  const {
    isOpen: isOpenDeleteCommissionReportDetail,
    onClose: onCloseDeleteCommissionReportDetail,
    onOpen: onOpenDeleteCommissionReportDetail,
  } = useDisclourse()

  const [isOpenDeductionsModal, setIsOpenDeductionsModal] = useState(false)
  const [calculateDeductions, {loading: loadingDeductions}] = useMutation(
    CALCULATE_DEDUCTIONS_COMMISSION_REPORT
  )
  const [loadingExport, setLoadingExport] = useState(false)

  const handleConfirmCalculateDeductions = async () => {
    try {
      const {data} = await calculateDeductions({variables: {id: parseId(report.id)}})
      if (data?.commissionReportCalculateDeductionsMutation?.success) {
        toast.success(intl.formatMessage({id: 'commissions.detail.deductions.toast.success', defaultMessage: '✅ Deductions calculated successfully'}))
      } else {
        toast.error(intl.formatMessage({id: 'commissions.detail.deductions.toast.failed', defaultMessage: '❌ Failed to calculate deductions'}))
      }
    } catch (error) {
      console.error(error)
      toast.error(intl.formatMessage({id: 'commissions.detail.deductions.toast.unexpected', defaultMessage: '⚠️ Unexpected error calculating deductions'}))
    } finally {
      setIsOpenDeductionsModal(false)
    }
  }

  // --- Filtros y paginación
  const [tabFilters] = useState('CommissionReportsDetails')
  const [valueUser, setValueUser] = useState<string[]>(
    isAgente && currentUserId ? [currentUserId] : []
  )
  // Antes del useState
  type IFilterStatus = 'all' | 'PENDING' | 'APPROVED' | 'NOT_ASSIGNED' | 'CANCELED'
  type IFilterType = 'all' | 'COMMISSION' | 'DEDUCTION' | 'CHARGEBACK' | 'BONUS'

  const [filterStatus, setFilterStatus] = useState<IFilterStatus>('all')
  const [filterType, setFilterType] = useState<IFilterType>('all')
  const [filterHasActiveClaims, setFilterHasActiveClaims] = useState<boolean | null>(null)
  const [filterCarrierId, setFilterCarrierId] = useState<string>('all')
  const [filterAssignedTo, setFilterAssignedTo] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(50) // Por defecto 50 elementos
  const [isPag] = useState(false)
  const [page, setPage] = useState(1)

  // --- Datos del detalle
  const [dataArr, setDataArr] = useState<
    IResCommissionReportsDetailsAPI['allCommissionReportsDetails']['edges']
  >([])
  const [commissionReportsDetailsSelected, setCommissionReportsDetailsSelected] = useState<
    {id: string; client: string; status: string; user: string}[] | undefined
  >([])

  const initialDetailData = {client: '', memberId: '', status: '', user: ''}
  const [detailData, setDetailData] = useState(initialDetailData)

  // --- Efecto para cargar reporte al inicio
  useEffect(() => {
    const decodedId = id ? String(parseId(id)) : null
    if (decodedId) {
      setReportId(decodedId)
      getOneCommissionReport({variables: {id: decodedId}})
    }
  }, [id, getOneCommissionReport])

  useEffect(() => {
    const memberId = searchParams.get('memberId') || ''

    if (client) {
      const decodedClient = decodeURIComponent(client)
      setDetailData((prev) => ({
        ...prev,
        client: decodedClient,
        memberId,
      }))
      return
    }

    if (memberId) {
      setDetailData((prev) => ({
        ...prev,
        memberId,
      }))
    }
  }, [client, searchParams])

  // --- Función para recargar reporte individual
  const refetchReport = async () => {
    if (reportId) {
      await getOneCommissionReport({
        variables: {id: reportId},
        fetchPolicy: 'no-cache',
      })
    }
  }

  // --- Obtener detalles del reporte con filtros
  const handleFilters = () => {
    const commissionReportId = id ? parseId(id) : null
    const userIds =
      valueUser && valueUser.length > 0 ? valueUser.map((userId) => parseId(userId)) : null

    gqlGetCommissionReportsDetails({
      variables: {
        first,
        offset: (currentPage - 1) * first,
        client: detailData.client || null,
        memberId: detailData.memberId || null,
        status: filterStatus === 'all' ? null : filterStatus,
        commissionType: filterType === 'all' ? null : filterType,
        user: userIds,
        commissionReport: commissionReportId,
        activeClaim: filterHasActiveClaims,
        carrierId: filterCarrierId === 'all' ? null : filterCarrierId,
        assignedTo: filterAssignedTo.length > 0 ? filterAssignedTo.map(id => String(parseId(id))) : null,
      },
    })
  }

  useEffect(() => {
    const shouldRun = reportId && (client ? detailData.client : true)
    if (shouldRun) {
      handleFilters()
      setCommissionReportsDetailsSelected([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reportId,
    first,
    currentPage,
    tabFilters,
    detailData.client, // importante que esto esté
    detailData.memberId,
  ])

  // --- Cargar datos de detalles y Validacion de totales
  const [totals, setTotals] = useState({
    pending: 0,
    approved: 0,
    notAssigned: 0,
    grand: 0,
    approvedWithPolicy: 0,
    approvedWithoutPolicy: 0,
  })

  useEffect(() => {
    if (!dataAllCommissionReportsDetails) return

    const details = dataAllCommissionReportsDetails.allCommissionReportsDetails

    // Actualizar la tabla de datos
    setDataArr(details.edges)

    // Actualizar los totales
    setTotals({
      pending: details.totalPending || 0,
      approved: details.totalApproved || 0,
      notAssigned: details.totalNotAssigned || 0,
      grand: details.grandTotal || 0,
      approvedWithPolicy: details.totalApprovedWithPolicy || 0,
      approvedWithoutPolicy: details.totalApprovedWithoutPolicy || 0,
    })
  }, [dataAllCommissionReportsDetails])

  // --- Manejo de errores
  useEffect(() => {
    if (error) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleClientName = () => {
    handleFilters()
  }

  const updatePage = (n: number) => setCurrentPage(n)

  const handleUpdateCommissionReportDetail = async (id: string, data: any) => {
    return await gqlUpdate({
      variables: {
        id: parseId(id),
        input: data,
      },
    })
  }

  const handleStatusChange = async (node, newStatus) => {
    const cleanData = removeKeysDeep(node, [
      'id',
      '__typename',
      'user',
      'policy',
      'commissionReport',
    ])
    cleanData.status = newStatus

    // Convertir stateEU de objeto a ID si existe
    if (cleanData.stateEU && typeof cleanData.stateEU === 'object') {
      cleanData.stateEU = parseId(cleanData.stateEU.id) || null
    }

    try {
      await handleUpdateCommissionReportDetail(node.id, cleanData)
      await refetchEntries()
    } catch (err) {
      toast.error(intl.formatMessage({id: 'commissions.detail.toast.statusError', defaultMessage: 'Error updating status'}))
    }
  }

  const removeKeysDeep = (obj: any, keysToRemove: string[] = ['__typename']): any => {
    if (Array.isArray(obj)) return obj.map((item) => removeKeysDeep(item, keysToRemove))
    if (obj && typeof obj === 'object') {
      const newObj: Record<string, any> = {}
      for (const key in obj) {
        if (!keysToRemove.includes(key)) {
          newObj[key] = removeKeysDeep(obj[key], keysToRemove)
        }
      }
      return newObj
    }
    return obj
  }

  const safeParseId = (value: any): string | null => {
    if (value === null || value === undefined || value === '') return null
    const parsed = parseId(value)
    if (!parsed || parsed === 0 || parsed === '0') return null
    return String(parsed)
  }

  const formatEffectivePeriod = (
    effectiveMonth?: number | null,
    effectiveYear?: number | null,
    monthFormat: 'MMM' | 'MMMM' = 'MMM'
  ): string | null => {
    const month = Number(effectiveMonth)
    const year = Number(effectiveYear)
    const hasMonth = Number.isFinite(month) && month > 0
    const hasYear = Number.isFinite(year) && year > 0

    if (hasMonth && hasYear) {
      return moment(`${year}-${month}-01`, 'YYYY-M-DD').format(`${monthFormat} YYYY`)
    }

    if (hasMonth) {
      return moment().month(month - 1).format(monthFormat)
    }

    if (hasYear) {
      return String(year)
    }

    return null
  }

  const handleDownloadDetails = async () => {
    if (!user?.token) return

    const currentReport = commissionReportById?.commissionReportById
    const commissionReportId = safeParseId(currentReport?.id)
    if (!currentReport || !commissionReportId) return

    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${user.token}`)

    const requestOptions = {
      method: 'GET',
      headers: myHeaders,
    }

    const rawParams: Record<string, string | null> = {
      id: commissionReportId,
      client: detailData.client || null,
      memberId: detailData.memberId || null,
      status: filterStatus === 'all' ? null : filterStatus,
      commissionType: filterType === 'all' ? null : filterType,
      user:
        valueUser && valueUser.length > 0
          ? valueUser.map((userId) => safeParseId(userId)).filter(Boolean).join(',')
          : null,
      carrierId: filterCarrierId === 'all' ? null : filterCarrierId,
      assignedTo:
        filterAssignedTo.length > 0
          ? filterAssignedTo.map((id) => safeParseId(id)).filter(Boolean).join(',')
          : null,
    }

    if (filterHasActiveClaims !== null) {
      rawParams.activeClaim = String(filterHasActiveClaims)
    }

    const params = new URLSearchParams()

    Object.entries(rawParams).forEach(([key, value]) => {
      if (value != null && value !== '') {
        params.append(key, value)
      }
    })

    setLoadingExport(true)
    try {
      const url = `${URL}/export-commission-details-excel?${params.toString()}`
      const response = await fetch(url, requestOptions)
      if (!response.ok) throw new Error(intl.formatMessage({id: 'commissions.logs.exportError', defaultMessage: 'Error exporting'}))
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `${currentReport.name} - details - ${moment().format('YY-MM-DD')}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: (err as {message: string}).message}))
    } finally {
      setLoadingExport(false)
    }
  }

  // --- Paginación
  const totalCommissionReportsDetails =
    dataAllCommissionReportsDetails?.allCommissionReportsDetails?.totalCount ?? 0

  const totalPages = Math.ceil(totalCommissionReportsDetails / first)

  const {start, end} = PaginationUtils.getPaginationRange(
    currentPage,
    first,
    totalCommissionReportsDetails
  )

  const {
    isOpen: isOpenSearchDetails,
    onOpen: onOpenSearchDetails,
    onClose: onCloseSearchDetails,
  } = useDisclourse()

  const floatingTabStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(50% - 70px)',
    right: 0,
    transform: 'translateY(-50%)',
    zIndex: 1051, // por encima del contenido
    borderRadius: '10px 0 0 10px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  }

  const filtersGridStyle: React.CSSProperties = {
    display: 'grid',
    gap: '0.45rem',
    width: '100%',
    alignItems: 'stretch',
  }

  const compactFilterControlStyle: React.CSSProperties = {
    width: '100%',
    height: '38px',
    minWidth: 0,
    borderRadius: '0.475rem',
    borderColor: '#dbdfe9',
    color: '#3f4254',
    fontSize: '0.95rem',
    fontWeight: 500,
    backgroundColor: '#fff',
  }

  const compactSearchButtonStyle: React.CSSProperties = {
    width: '100%',
    height: '38px',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  }

  const agentMenuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  const agentSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: '38px',
      height: '38px',
      borderRadius: '0.475rem',
      borderColor: '#dbdfe9',
      boxShadow: 'none',
      backgroundColor: '#fff',
    }),
    valueContainer: (base: any) => ({
      ...base,
      minHeight: '38px',
      padding: '0 12px',
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: '#3f4254',
      fontSize: '0.95rem',
      fontWeight: 500,
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#3f4254',
      margin: 0,
      fontSize: '0.95rem',
      fontWeight: 500,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#3f4254',
      fontSize: '0.95rem',
      fontWeight: 500,
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      fontSize: '0.9rem',
      fontWeight: 500,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: '38px',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    clearIndicator: (base: any) => ({
      ...base,
      padding: '0 8px',
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 2000,
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 2000,
    }),
  }

  // --- Gestion de upload de excel

  const [uploadFile, {data: dataFile, error: errorFile, loading: loadingFile}] =
    useUploadFile<IResUploadPaymentCommissionReport>()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadFileNameRef = useRef<string>('')
  const pendingFileRef = useRef<File | null>(null)
  const [isOpenUploadTypeModal, setIsOpenUploadTypeModal] = useState(false)
  const [uploadType, setUploadType] = useState<'commission' | 'bonus'>('commission')

  useEffect(() => {
    if (errorFile) toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: errorFile}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorFile])

  useEffect(() => {
    const result = dataFile?.commissionReportUploadPaymentMutation
    if (!result) return

    const {success, message, errors} = result

    if (success) {
      toast.success(
        intl.formatMessage(
          {id: 'commissions.detail.upload.toast.success', defaultMessage: 'Payment file "{name}" uploaded successfully'},
          {name: uploadFileNameRef.current || 'commission-report-payment.xlsx'}
        )
      )
      uploadFileNameRef.current = ''
      refetchEntries()
    } else {
      const errorMessage =
        errors
          ?.map((err) => err?.message)
          .filter(Boolean)
          .join(', ') ||
        message ||
        intl.formatMessage({id: 'commissions.detail.upload.somethingWrong', defaultMessage: 'Something went wrong during upload.'})
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: errorMessage}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFile, refetchEntries])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0]
    if (!file) return

    // Guardar el archivo y abrir modal de confirmación
    pendingFileRef.current = file
    setUploadType('commission') // Reset to default
    setIsOpenUploadTypeModal(true)

    event.target.value = ''
  }

  const handleConfirmUpload = () => {
    const file = pendingFileRef.current
    if (!file) return

    uploadFileNameRef.current = file.name

    const formdata = new FormData()
    formdata.append('0', file, file.name)
    formdata.append('map', '{"0": ["variables.excelFile"]}')
    const bonusSuffix = uploadType === 'bonus' ? intl.formatMessage({id: 'commissions.detail.upload.asBonusSuffix', defaultMessage: ' (as BONUS)'}) : ''
    toast.info(intl.formatMessage({id: 'commissions.detail.upload.starting', defaultMessage: 'Starting upload: {name}{suffix}'}, {name: file.name, suffix: bonusSuffix}))
    if (!id) {
      toast.error(intl.formatMessage({id: 'commissions.detail.upload.missingId', defaultMessage: 'Missing report ID'}))
      return
    }

    const commissionReportId = parseId(id)

    const obj = {
      query: UPLOAD_PAYMENT_COMMISSION_REPORT,
      variables: {
        id: commissionReportId,
        excelFile: null,
        uploadType: uploadType,
      },
    }
    formdata.append('operations', JSON.stringify(obj))
    uploadFile(formdata)

    pendingFileRef.current = null
    setIsOpenUploadTypeModal(false)
  }

  const handleButtonClick = () => {
    fileInputRef?.current?.click()
  }

  const report = commissionReportById?.commissionReportById ?? null

  // --- Render principal ---
  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3' onClick={(e) => navigate('/commissions')}>
            <FormattedMessage id='commissions.reports.payroll.breadcrumbReports' defaultMessage='Reports List' />
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'><FormattedMessage id='commissions.detail.breadcrumbDetailed' defaultMessage='Detailed' /></Breadcrumb.Item>
        </Breadcrumb>
        <div className='policies-summary summary-cards mb-3'>
          <Card className='card-summary green flex-fill mx-2 text-center'>
            <Card.Title><FormattedMessage id='commissions.detail.payroll.approvedTotal' defaultMessage='Approved Total Amount' /></Card.Title>
            <Card.Body>
              {!report ? (
                <span className='placeholder-glow d-block'><span className='placeholder rounded' style={{width: 80, height: 22, display: 'inline-block'}} /></span>
              ) : loading ? (
                <Spinner animation='border' />
              ) : (
                parsePrice(totals.approved ?? 0)
              )}
            </Card.Body>
          </Card>
          <Card className='card-summary warning flex-fill mx-2 text-center'>
            <Card.Title><FormattedMessage id='commissions.detail.pendingTotal' defaultMessage='Pending Total Amount' /></Card.Title>
            <Card.Body>
              {!report ? (
                <span className='placeholder-glow d-block'><span className='placeholder rounded' style={{width: 80, height: 22, display: 'inline-block'}} /></span>
              ) : loading ? (
                <Spinner animation='border' />
              ) : (
                parsePrice(totals.pending ?? 0)
              )}
            </Card.Body>
          </Card>
          <Card className='card-summary canceled flex-fill mx-2 text-center'>
            <Card.Title><FormattedMessage id='commissions.detail.notAssignedTotal' defaultMessage='Not Assigned Total Amount' /></Card.Title>
            <Card.Body>
              {!report ? (
                <span className='placeholder-glow d-block'><span className='placeholder rounded' style={{width: 80, height: 22, display: 'inline-block'}} /></span>
              ) : loading ? (
                <Spinner animation='border' />
              ) : (
                parsePrice(totals.notAssigned ?? 0)
              )}
            </Card.Body>
          </Card>
        </div>
      </header>

      <Row className='commissionReports'>
        <Col className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>
                {!report ? (
                  <span className='placeholder-glow'>
                    <span className='placeholder rounded d-block mb-2' style={{width: 220, height: 20}} />
                    <span className='placeholder rounded d-block' style={{width: 150, height: 14}} />
                  </span>
                ) : (
                  <>
                    <FormattedMessage id='commissions.logs.reportName' defaultMessage='Report: {name}' values={{name: report.name}} />{' '}
                    {canEditCommission && !isAgente && (
                      <span
                        style={{cursor: 'pointer'}}
                        className='text-primary ms-2'
                        onClick={() => {
                          setInfoEdit(report)
                          setIsOpenEdit(true)
                        }}
                      >
                        <i className='bi bi-pencil-square' /> <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                      </span>
                    )}
                    <br />
                    <small>
                      <FormattedMessage id='commissions.logs.date' defaultMessage='Date:' /> {formatEffectivePeriod(report.effectiveMonth, report.effectiveYear, 'MMMM') || ''}
                    </small>
                  </>
                )}
              </h3>

              <div className='d-flex gap-2 align-items-center'>
                {report && canEditCommission && (
                  <>
                    {report.status === 'DRAFT' && (
                      <Button
                        size='sm'
                        onClick={handleButtonClick}
                        disabled={loadingFile}
                      >
                        {loadingFile ? (
                          <>
                            <Spinner animation='border' size='sm' className='me-2' />
                            <FormattedMessage id='commissions.reports.uploading' defaultMessage='Uploading...' />
                          </>
                        ) : (
                          <>
                            <i className='bi bi-upload fs-6' /> <FormattedMessage id='commissions.detail.uploadFile' defaultMessage='Upload File' />
                          </>
                        )}
                      </Button>
                    )}
                    <input
                      type='file'
                      ref={fileInputRef}
                      style={{display: 'none'}}
                      accept='.xlsx,.xls'
                      onChange={handleFileChange}
                    />

                    {!isAgente && (
                      <Button
                        size='sm'
                        onClick={() => navigate(`/commissions/${report.id}/conciliation`)}
                      >
                        {report.status === 'DRAFT' ? (
                          <>
                            <i className='bi bi-list-check' /> <FormattedMessage id='commissions.logs.conciliation' defaultMessage='Conciliation' />
                          </>
                        ) : (
                          <>
                            <i className='bi bi-list-check' /> <FormattedMessage id='commissions.detail.logsConciliated' defaultMessage='Logs conciliated' />
                          </>
                        )}
                      </Button>
                    )}

                    {report.status === 'DRAFT' && (
                      <Button
                        size='sm'
                        onClick={() => {
                          onOpen()
                        }}
                      >
                        <i className='bi bi-plus fs-3' /> <FormattedMessage id='commissions.detail.addRecord' defaultMessage='Add record' />
                      </Button>
                    )}

                    {report.status === 'DRAFT' && (
                      <Button
                        size='sm'
                        disabled={loadingDeductions}
                        onClick={() => setIsOpenDeductionsModal(true)}
                      >
                        {loadingDeductions ? (
                          <Spinner animation='border' size='sm' />
                        ) : (
                          <>
                            <i className='bi bi-calculator' /> <FormattedMessage id='commissions.detail.calculateDeductions' defaultMessage='Calculate Deductions' />
                          </>
                        )}
                      </Button>
                    )}
                  </>
                )}
                <Button size='sm' disabled={loadingExport || !report} onClick={handleDownloadDetails}>
                  {loadingExport ? (
                    <>
                      <Spinner animation='border' size='sm' />{' '}
                    </>
                  ) : (
                    <i className='bi bi-download'></i>
                  )}
                  <FormattedMessage id='commissions.logs.export' defaultMessage='Export' />
                </Button>
                <Button size='sm' disabled={!report} onClick={() => report && navigate(`/commissions/${report.id}/payroll`)}>
                  <>
                    <i className='bi bi-cash-coin me-1' /> <FormattedMessage id='commissions.reports.payroll' defaultMessage='Payroll' />
                  </>
                </Button>
                {report && canEditCommission && !isAgente && (
                  <>
                    <Button
                      size='sm'
                      variant={report.status === 'DRAFT' ? 'danger' : 'success'}
                      className='ms-2'
                      onClick={() => {
                        setToggleAction(report.status === 'DRAFT' ? 'close' : 'reopen')
                        setIsOpenToggleModal(true)
                      }}
                    >
                      {report.status === 'DRAFT' ? (
                        <>
                          <i className='bi bi-lock-fill' /> <FormattedMessage id='commissions.detail.payroll.finishClose' defaultMessage='Finish and Close Report' />
                        </>
                      ) : (
                        <>
                          <i className='bi bi-unlock-fill' /> <FormattedMessage id='commissions.reports.reopen.title' defaultMessage='Reopen Report' />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className='filters-header'>
              <div className='d-flex flex-column gap-3'>
                <div style={{
                  ...filtersGridStyle,
                  gridTemplateColumns: (() => {
                    const hasCarrierFilter = (report?.carriers?.length ?? 0) > 1
                    const hasAgentFilter = !isAgente
                    const hasAssignedToFilter = !isAgente
                    const hasActiveClaimsFilter = report?.status === 'COMPLETED'
                    const cols: string[] = [
                      'minmax(0, 2.5fr)',   // client search — siempre
                      'minmax(0, 1.4fr)',   // status — siempre
                      ...(hasCarrierFilter ? ['minmax(0, 1.6fr)'] : []),    // carrier
                      'minmax(0, 1.4fr)',   // type — siempre
                      ...(hasAgentFilter ? ['minmax(0, 2fr)'] : []),         // agent
                      ...(hasAssignedToFilter ? ['minmax(0, 2fr)'] : []),    // assigned to
                      ...(hasActiveClaimsFilter ? ['auto'] : []),             // active claims
                      'minmax(90px, 0.7fr)', // search button — siempre
                    ]
                    return cols.join(' ')
                  })(),
                }}>
                  <div style={{minWidth: 0}}>
                    <Form.Control
                      placeholder={intl.formatMessage({id: 'commissions.logs.filterPlaceholder', defaultMessage: 'Filter by client name or member ID'})}
                      aria-label={intl.formatMessage({id: 'commissions.logs.filterAriaLabel', defaultMessage: "Client's name or member ID with two button addons"})}
                      onChange={(e) =>
                        setDetailData((prev) => ({
                          ...prev,
                          client: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleClientName()
                        }
                      }}
                      value={detailData.client}
                      disabled={false}
                      style={compactFilterControlStyle}
                    />
                  </div>
                  <div style={{minWidth: 0}}>
                    <Form.Select
                      value={filterStatus}
                      aria-label={intl.formatMessage({id: 'commissions.logs.filterByStatusAria', defaultMessage: 'Filter by status'})}
                      onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                      style={compactFilterControlStyle}
                    >
                      <option value='all'>{intl.formatMessage({id: 'commissions.logs.filterByStatus', defaultMessage: 'Filter by Status'})}</option>
                      <option value='PENDING'>{intl.formatMessage({id: 'commissions.detail.status.pending', defaultMessage: 'Pending'})}</option>
                      <option value='APPROVED'>{intl.formatMessage({id: 'commissions.detail.status.approved', defaultMessage: 'Approved'})}</option>
                      <option value='NOT_ASSIGNED'>{intl.formatMessage({id: 'commissions.detail.status.notAssigned', defaultMessage: 'Not assigned'})}</option>
                      <option value='CANCELED'>{intl.formatMessage({id: 'commissions.detail.status.canceled', defaultMessage: 'Canceled'})}</option>
                    </Form.Select>
                  </div>
                  {report?.carriers && report.carriers.length > 1 && (
                    <div style={{minWidth: 0}}>
                      <Form.Select
                        value={filterCarrierId}
                        aria-label={intl.formatMessage({id: 'commissions.reports.filterByCarrier', defaultMessage: 'Filter by carrier'})}
                        onChange={(e) => setFilterCarrierId(e.target.value)}
                        style={compactFilterControlStyle}
                      >
                        <option value='all'>{intl.formatMessage({id: 'commissions.detail.allCompanies', defaultMessage: 'All Companies'})}</option>
                        {report.carriers.map((c) => (
                          <option key={c.id} value={String(parseId(c.id))}>
                            {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  )}
                  <div style={{minWidth: 0}}>
                    <Form.Select
                      value={filterType}
                      aria-label={intl.formatMessage({id: 'commissions.detail.payroll.filterByType', defaultMessage: 'Filter by Type'})}
                      onChange={(e) => setFilterType(e.target.value as IFilterType)}
                      style={compactFilterControlStyle}
                    >
                      <option value='all'>{intl.formatMessage({id: 'commissions.detail.payroll.filterByType', defaultMessage: 'Filter by Type'})}</option>
                      <option value='COMMISSION'>{intl.formatMessage({id: 'commissions.detail.commissionType.commission', defaultMessage: 'Commission'})}</option>
                      <option value='DEDUCTION'>{intl.formatMessage({id: 'commissions.detail.commissionType.deduction', defaultMessage: 'Deduction'})}</option>
                      <option value='CHARGEBACK'>{intl.formatMessage({id: 'commissions.detail.commissionType.chargeback', defaultMessage: 'Chargeback'})}</option>
                      <option value='BONUS'>{intl.formatMessage({id: 'commissions.detail.commissionType.bonus', defaultMessage: 'Bonus'})}</option>
                    </Form.Select>
                  </div>
                  {!isAgente && (
                    <div style={{minWidth: 0}}>
                      <MultiSelectWithActions
                        value={
                          valueUser && valueUser.length > 0
                            ? dataMembers?.allUsers.edges
                                .filter(({node}) => valueUser.includes(node.id))
                                .map(({node}) => ({
                                  value: node.id,
                                  label: `${node.firstName} ${node.lastName} - ${node.email}${
                                    node.npn ? ` (${node.npn})` : ''
                                  }`,
                                }))
                            : []
                        }
                        options={
                          dataMembers?.allUsers.edges.map(({node}) => ({
                            value: node.id,
                            label: `${node.firstName} ${node.lastName} - ${node.email}${
                              node.npn ? ` (${node.npn})` : ''
                            }`,
                          })) || []
                        }
                        onChange={(selected: any) => {
                          const selectedValues = Array.isArray(selected)
                            ? selected.map((opt: any) => opt.value)
                            : []
                          setValueUser(selectedValues)
                        }}
                        placeholder={intl.formatMessage({id: 'commissions.reports.payroll.filterByAgent', defaultMessage: 'Filter by Agent'})}
                        menuPortalTarget={agentMenuPortalTarget}
                        menuPosition='fixed'
                        styles={agentSelectStyles}
                      />
                    </div>
                  )}
                  {!isAgente && (
                    <div style={{minWidth: 0}}>
                      {(() => {
                        const assignedToOptions = (dataAssignedToUsers?.allUsersList ?? []).map((u) => ({
                          value: u.id,
                          label: `${u.firstName} ${u.lastName} - ${u.email}${u.npn ? ` (${u.npn})` : ''}`,
                        }))
                        const selectedAssignedToOptions = filterAssignedTo.length > 0
                          ? assignedToOptions.filter((opt) => filterAssignedTo.includes(String(opt.value)))
                          : null
                        return (
                          <MultiSelectWithActions
                            value={selectedAssignedToOptions}
                            options={assignedToOptions}
                            onChange={(selected: any) => {
                              const values = Array.isArray(selected)
                                ? selected.map((opt: any) => opt?.value).filter((v: any) => v !== '')
                                : []
                              setFilterAssignedTo(values)
                            }}
                            placeholder={intl.formatMessage({id: 'commissions.detail.filterAssignedTo', defaultMessage: 'Filter by Assigned To'})}
                            menuPortalTarget={agentMenuPortalTarget}
                            menuPosition='fixed'
                            styles={agentSelectStyles}
                          />
                        )
                      })()}
                    </div>
                  )}
                  {report?.status === 'COMPLETED' && (
                    <div style={{minWidth: 0, display: 'flex', alignItems: 'center'}}>
                      <Form.Check
                        type='checkbox'
                        id='hasActiveClaims'
                        label={intl.formatMessage({id: 'commissions.reports.hasActiveClaims', defaultMessage: 'Has Active Claims'})}
                        checked={filterHasActiveClaims || false}
                        onChange={(e) => {
                          setFilterHasActiveClaims(e.target.checked ? true : null)
                        }}
                      />
                    </div>
                  )}
                  <div style={{minWidth: 0}}>
                    <Button
                      className='w-100'
                      disabled={false}
                      onClick={() => handleFilters()}
                      variant='outline-success'
                      style={{...compactSearchButtonStyle, border: '1px solid #dbdfe9'}}
                    >
                      <FormattedMessage id='common.action.search' defaultMessage='Search' />
                    </Button>
                  </div>
                </div>

              </div>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalCommissionReportsDetails}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
                pageSizeOptions={[50, 100, 500]}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'><FormattedMessage id='commissions.logs.field.client' defaultMessage='Client' /></th>
                    <th className='fw-bold'><FormattedMessage id='common.field.agent' defaultMessage='Agent' /></th>
                    {!isAgente && <th className='fw-bold'><FormattedMessage id='commissions.detail.assignedTo' defaultMessage='Assigned To' /></th>}
                    <th className='fw-bold'><FormattedMessage id='common.field.type' defaultMessage='Type' /></th>
                    <th className='fw-bold'><FormattedMessage id='common.field.status' defaultMessage='Status' /></th>
                    <th className='fw-bold'><FormattedMessage id='commissions.logs.field.rate' defaultMessage='Rate' /></th>
                    <th className='fw-bold'><FormattedMessage id='common.field.members' defaultMessage='Members' /></th>
                    <th className='fw-bold'><FormattedMessage id='commissions.logs.field.subtotal' defaultMessage='Subtotal' /></th>
                    <th className='fw-bold'><FormattedMessage id='common.field.actions' defaultMessage='Actions' /></th>
                  </tr>
                </thead>
                {(!report || loading || !dataAllCommissionReportsDetails) && !isPag ? (
                  <tbody>
                    {Array.from({length: 8}).map((_, i) => (
                      <tr key={i} className='placeholder-glow'>
                        <td>
                          <span className='placeholder rounded d-block mb-1' style={{width: '75%', height: 14}} />
                          <span className='placeholder rounded d-block' style={{width: '50%', height: 12}} />
                        </td>
                        <td><span className='placeholder rounded' style={{width: 90, height: 14, display: 'inline-block'}} /></td>
                        {!isAgente && <td><span className='placeholder rounded' style={{width: 90, height: 14, display: 'inline-block'}} /></td>}
                        <td><span className='placeholder rounded' style={{width: 70, height: 14, display: 'inline-block'}} /></td>
                        <td><span className='placeholder rounded' style={{width: 65, height: 22, display: 'inline-block', borderRadius: 12}} /></td>
                        <td><span className='placeholder rounded' style={{width: 50, height: 14, display: 'inline-block'}} /></td>
                        <td><span className='placeholder rounded' style={{width: 30, height: 14, display: 'inline-block'}} /></td>
                        <td><span className='placeholder rounded' style={{width: 60, height: 14, display: 'inline-block'}} /></td>
                        <td><span className='placeholder rounded' style={{width: 55, height: 28, display: 'inline-block'}} /></td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    {(loading || dataArr.length === 0) && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            {loading ? (
                              <Spinner animation='border' />
                            ) : (
                              intl.formatMessage({id: 'commissions.detail.empty', defaultMessage: 'There are no commission report details currently added'})
                            )}
                          </div>
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      (() => {
                        const startIndex = first * page - first
                        const endIndex = first * page
                        const currentPageItems = dataArr.slice(startIndex, endIndex)

                        const withPolicy = currentPageItems.filter(({node}) => node.policy)
                        const withoutPolicy = currentPageItems.filter(({node}) => !node.policy)

                        // Si el reporte está COMPLETED, no separar por secciones
                        const isDraft = report.status === 'DRAFT'

                        return (
                          <>
                            {isDraft && withoutPolicy.length > 0 && (
                              <>
                                <tr>
                                  <td
                                    colSpan={8}
                                    style={{
                                      backgroundColor: '#337ab7',
                                      color: 'black',
                                      textAlign: 'left',
                                      padding: '8px',
                                    }}
                                  >
                                    <strong><FormattedMessage id='commissions.detail.rowsAddedManually' defaultMessage='Rows added manually' /></strong>
                                  </td>
                                </tr>
                                {withoutPolicy.map(({node}) => (
                                  <tr key={node.id}>
                                    {/* Repite aquí el mismo contenido que arriba */}
                                    <td>
                                      <div>
                                        <span
                                          className='text-primary'
                                          style={{cursor: 'pointer'}}
                                          data-tooltip-id={`myt2${parseId(node.id)}`}
                                        >
                                          {node.client}
                                          {node.activeClaim && (
                                            <i
                                              className='bi bi-exclamation-circle-fill text-danger'
                                              title={intl.formatMessage({id: 'commissions.detail.activeClaimTooltip', defaultMessage: 'This client has an active claim'})}
                                            />
                                          )}
                                        </span>
                                        {(node.memberId || node.stateEU?.code) && (
                                          <>
                                            {node.memberId && <p className='m-0'><FormattedMessage id='commissions.logs.row.memberId' defaultMessage='Member ID: {id}' values={{id: node.memberId}} /></p>}
                                            {node.stateEU?.code && <p className='m-0'><FormattedMessage id='commissions.logs.row.state' defaultMessage='State: {code}' values={{code: node.stateEU?.code}} /></p>}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <span className='text-primary'>
                                          {node.user.firstName} {node.user.lastName}
                                        </span>
                                        <p style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                          <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.user.npn}} />
                                        </p>
                                      </div>
                                    </td>
                                    {!isAgente && (
                                      <td>
                                        {node.assignedTo ? (
                                          <div>
                                            <span>
                                              {node.assignedTo.firstName} {node.assignedTo.lastName}
                                            </span>
                                            {node.assignedTo.npn && (
                                              <p className='m-0' style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                                <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.assignedTo.npn}} />
                                              </p>
                                            )}
                                          </div>
                                        ) : (
                                          <span className='text-muted'>{emDash}</span>
                                        )}
                                      </td>
                                    )}
                                    <td>
                                      <div>
                                        <span
                                          className={`commission-type ${node.commissionType.toLowerCase()}`}
                                          style={{maxWidth: '140px', wordBreak: 'break-all'}}
                                        >
                                          {commissionTypeLabel(node.commissionType)}
                                        </span>
                                        {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear) && (
                                          <p className='m-0'>
                                            <FormattedMessage id='commissions.detail.monthLabel' defaultMessage='Month:' />{space}
                                            {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear)}
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <BadgeStatus state={node.status} />
                                    </td>
                                    <td>{parsePrice(node.rate)}</td>
                                    <td>{node.members}</td>
                                    <td>{parsePrice(node.subtotal)}</td>
                                    <td
                                      className='d-flex gap-2 align-items-center'
                                      style={{minWidth: '120px'}}
                                    >
                                      {canEditCommission && report.status === 'DRAFT' && (
                                        <>
                                          {/* Icono de check (verde) */}
                                          {(node.status === 'PENDING' ||
                                            node.status === 'NOT_ASSIGNED') && (
                                              <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                  <Tooltip id={`tooltip-approve-${node.id}`}>
                                                    <FormattedMessage id='commissions.detail.markApproved' defaultMessage='Mark as Approved' />
                                                  </Tooltip>
                                                }
                                              >
                                                <i
                                                  className='bi bi-check-square-fill text-success'
                                                  style={{cursor: 'pointer', fontSize: '1.2rem'}}
                                                  onClick={() =>
                                                    handleStatusChange(node, 'APPROVED')
                                                  }
                                                />
                                              </OverlayTrigger>
                                            )}

                                          {/* Icono de X (rojo) */}
                                          {(node.status === 'PENDING' ||
                                            node.status === 'APPROVED') && (
                                              <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                  <Tooltip id={`tooltip-notassigned-${node.id}`}>
                                                    <FormattedMessage id='commissions.detail.markNotAssigned' defaultMessage='Mark as Not Assigned' />
                                                  </Tooltip>
                                                }
                                              >
                                                <i
                                                  className='bi bi-x-square-fill text-danger'
                                                  style={{cursor: 'pointer', fontSize: '1.2rem'}}
                                                  onClick={() =>
                                                    handleStatusChange(node, 'NOT_ASSIGNED')
                                                  }
                                                />
                                              </OverlayTrigger>
                                            )}
                                          <Dropdown as={ButtonGroup}>
                                            <Button
                                              variant='outline-primary'
                                              size='sm'
                                              style={{border: '1px solid #3e97ff'}}
                                            >
                                              <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                                            </Button>
                                            <Dropdown.Toggle
                                              split
                                              variant='outline-primary'
                                              size='sm'
                                              id='dropdown-custom-2'
                                              style={{border: '1px solid #3e97ff'}}
                                            />
                                            <Dropdown.Menu className='dropdown-menu-commissionReport'>
                                              {(node.status === 'APPROVED' ||
                                                node.status === 'NOT_ASSIGNED') && (
                                                <Dropdown.Item
                                                  eventKey='mark-pending'
                                                  onClick={() =>
                                                    handleStatusChange(node, 'PENDING')
                                                  }
                                                >
                                                  <i className='bi bi-check-square-fill' /> <FormattedMessage id='commissions.detail.markPending' defaultMessage='Mark as Pending' />
                                                </Dropdown.Item>
                                              )}
                                              <Dropdown.Item
                                                eventKey='editDetail'
                                                onClick={(e) => {
                                                  e.preventDefault()
                                                  setInfoEditDetail(node)
                                                  setIsOpenEditDetail(true)
                                                }}
                                              >
                                                <i className='bi bi-pencil-square' /> <FormattedMessage id='commissions.logs.action.update' defaultMessage='Update' />
                                              </Dropdown.Item>

                                              <Dropdown.Item
                                                eventKey='reassign'
                                                onClick={() => {
                                                  setInfoEditDetail(node)
                                                  setIsOpenAssignAgent(true)
                                                }}
                                              >
                                                <i className='bi bi-pencil-fill' /> <FormattedMessage id='commissions.logs.action.reassign' defaultMessage='Reassign' />
                                              </Dropdown.Item>

                                              <Dropdown.Divider />
                                              <Dropdown.Item
                                                eventKey='delete'
                                                onClick={() => {
                                                  onOpenDeleteCommissionReportDetail()
                                                  setInfoEditDetail(node)
                                                }}
                                              >
                                                <i className='bi bi-check-square-fill' /> <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                                              </Dropdown.Item>
                                            </Dropdown.Menu>
                                          </Dropdown>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                <tr>
                                  <td
                                    colSpan={6}
                                    style={{
                                      backgroundColor: '#9fddaeff',
                                      color: 'black',
                                      textAlign: 'right',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    <FormattedMessage id='commissions.detail.totalApprovedToPay' defaultMessage='Total Approved to pay:' />
                                  </td>
                                  <td
                                    colSpan={2}
                                    style={{
                                      backgroundColor: '#9fddaeff',
                                      color: 'black',
                                      textAlign: 'left',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {parsePrice(totals.approvedWithoutPolicy)}
                                  </td>
                                </tr>
                              </>
                            )}

                            {/* Si es DRAFT, mostrar sección con carrier name. Si es COMPLETED, solo los registros */}
                            {isDraft && withPolicy.length > 0 && (
                              <>
                                <tr>
                                  <td
                                    colSpan={8}
                                    style={{
                                      backgroundColor: '#337ab7',
                                      color: 'black',
                                      textAlign: 'left',
                                      padding: '8px',
                                    }}
                                  >
                                    <strong>
                                      {report.carriers && report.carriers.length > 0
                                        ? report.carriers.map(c => c.name).join(' / ')
                                        : report.carrier?.name || intl.formatMessage({id: 'commissions.detail.unknownCarrier', defaultMessage: 'Unknown'})}
                                    </strong>
                                  </td>
                                </tr>
                                {withPolicy.map(({node}) => (
                                  <tr key={node.id}>
                                    <td>
                                      <div>
                                        <span
                                          className='text-primary'
                                          style={{cursor: 'pointer'}}
                                          data-tooltip-id={`myt2${parseId(node.id)}`}
                                        >
                                          {node.client}
                                          {node.activeClaim && (
                                            <i
                                              className='bi bi-exclamation-circle-fill text-danger'
                                              title={intl.formatMessage({id: 'commissions.detail.activeClaimTooltip', defaultMessage: 'This client has an active claim'})}
                                            />
                                          )}
                                        </span>
                                        <p className='m-0'><FormattedMessage id='commissions.logs.row.memberId' defaultMessage='Member ID: {id}' values={{id: node.memberId}} /></p>
                                        <p className='m-0'><FormattedMessage id='commissions.logs.row.state' defaultMessage='State: {code}' values={{code: node.stateEU?.code}} /></p>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <span className='text-primary'>
                                          {node.user.firstName} {node.user.lastName}
                                          <p style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                            <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.user.npn}} />
                                          </p>
                                        </span>
                                      </div>
                                    </td>
                                    {!isAgente && (
                                      <td>
                                        {node.assignedTo ? (
                                          <div>
                                            <span>
                                              {node.assignedTo.firstName} {node.assignedTo.lastName}
                                            </span>
                                            {node.assignedTo.npn && (
                                              <p className='m-0' style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                                <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.assignedTo.npn}} />
                                              </p>
                                            )}
                                          </div>
                                        ) : (
                                          <span className='text-muted'>{emDash}</span>
                                        )}
                                      </td>
                                    )}
                                    <td>
                                      <div>
                                        <span
                                          className={`commission-type ${node.commissionType.toLowerCase()}`}
                                          style={{maxWidth: '140px', wordBreak: 'break-all'}}
                                        >
                                          {commissionTypeLabel(node.commissionType)}
                                        </span>
                                        {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear) && (
                                          <p className='m-0'>
                                            <FormattedMessage id='commissions.detail.monthLabel' defaultMessage='Month:' />{space}
                                            {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear)}
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <BadgeStatus state={node.status} />
                                    </td>
                                    <td>{parsePrice(node.rate)}</td>
                                    <td>{node.members}</td>
                                    <td>{parsePrice(node.subtotal)}</td>
                                    <td
                                      className='d-flex gap-2 align-items-center'
                                      style={{minWidth: '120px'}}
                                    >
                                      {canEditCommission && report.status === 'DRAFT' && (
                                        <>
                                          {(node.status === 'PENDING' ||
                                            node.status === 'NOT_ASSIGNED') && (
                                              <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                  <Tooltip id={`tooltip-approve-${node.id}`}>
                                                    <FormattedMessage id='commissions.detail.markApproved' defaultMessage='Mark as Approved' />
                                                  </Tooltip>
                                                }
                                              >
                                                <i
                                                  className='bi bi-check-square-fill text-success'
                                                  style={{cursor: 'pointer', fontSize: '1.2rem'}}
                                                  onClick={() =>
                                                    handleStatusChange(node, 'APPROVED')
                                                  }
                                                />
                                              </OverlayTrigger>
                                            )}

                                          {(node.status === 'PENDING' ||
                                            node.status === 'APPROVED') && (
                                              <OverlayTrigger
                                                placement='top'
                                                overlay={
                                                  <Tooltip id={`tooltip-notassigned-${node.id}`}>
                                                    <FormattedMessage id='commissions.detail.markNotAssigned' defaultMessage='Mark as Not Assigned' />
                                                  </Tooltip>
                                                }
                                              >
                                                <i
                                                  className='bi bi-x-square-fill text-danger'
                                                  style={{cursor: 'pointer', fontSize: '1.2rem'}}
                                                  onClick={() =>
                                                    handleStatusChange(node, 'NOT_ASSIGNED')
                                                  }
                                                />
                                              </OverlayTrigger>
                                            )}
                                          <Dropdown as={ButtonGroup}>
                                            <Button
                                              variant='outline-primary'
                                              size='sm'
                                              style={{border: '1px solid #3e97ff'}}
                                            >
                                              <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                                            </Button>
                                            <Dropdown.Toggle
                                              split
                                              variant='outline-primary'
                                              size='sm'
                                              id='dropdown-custom-2'
                                              style={{border: '1px solid #3e97ff'}}
                                            />
                                            <Dropdown.Menu className='dropdown-menu-commissionReport'>
                                              {(node.status === 'APPROVED' ||
                                                node.status === 'NOT_ASSIGNED') && (
                                                <Dropdown.Item
                                                  eventKey='mark-pending'
                                                  onClick={() =>
                                                    handleStatusChange(node, 'PENDING')
                                                  }
                                                >
                                                  <i className='bi bi-check-square-fill' /> <FormattedMessage id='commissions.detail.markPending' defaultMessage='Mark as Pending' />
                                                </Dropdown.Item>
                                              )}
                                              <Dropdown.Item
                                                eventKey='editDetail'
                                                onClick={(e) => {
                                                  e.preventDefault()
                                                  setInfoEditDetail(node)
                                                  setIsOpenEditDetail(true)
                                                }}
                                              >
                                                <i className='bi bi-pencil-square' /> <FormattedMessage id='commissions.logs.action.update' defaultMessage='Update' />
                                              </Dropdown.Item>

                                              <Dropdown.Item
                                                eventKey='reassign'
                                                onClick={() => {
                                                  setInfoEditDetail(node)
                                                  setIsOpenAssignAgent(true)
                                                }}
                                              >
                                                <i className='bi bi-pencil-fill' /> <FormattedMessage id='commissions.logs.action.reassign' defaultMessage='Reassign' />
                                              </Dropdown.Item>

                                              <Dropdown.Divider />
                                              <Dropdown.Item
                                                eventKey='delete'
                                                onClick={() => {
                                                  onOpenDeleteCommissionReportDetail()
                                                  setInfoEditDetail(node)
                                                }}
                                              >
                                                <i className='bi bi-check-square-fill' /> <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                                              </Dropdown.Item>
                                            </Dropdown.Menu>
                                          </Dropdown>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                                <tr>
                                  <td
                                    colSpan={6}
                                    style={{
                                      backgroundColor: '#9fddaeff',
                                      color: 'black',
                                      textAlign: 'right',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    <FormattedMessage id='commissions.detail.totalApprovedToPay' defaultMessage='Total Approved to pay:' />
                                  </td>
                                  <td
                                    colSpan={2}
                                    style={{
                                      backgroundColor: '#9fddaeff',
                                      color: 'black',
                                      textAlign: 'left',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {parsePrice(totals.approvedWithPolicy)}
                                  </td>
                                </tr>
                              </>
                            )}

                            {/* Si el reporte está COMPLETED, mostrar todos los registros sin separar */}
                            {!isDraft && currentPageItems.map(({node}) => (
                              <tr key={node.id}>
                                <td>
                                  <div>
                                    <span
                                      className='text-primary'
                                      style={{cursor: 'pointer'}}
                                      data-tooltip-id={`myt2${parseId(node.id)}`}
                                    >
                                      {node.client}
                                      {node.activeClaim && (
                                        <i
                                          className='bi bi-exclamation-circle-fill text-danger'
                                          title={intl.formatMessage({id: 'commissions.detail.activeClaimTooltip', defaultMessage: 'This client has an active claim'})}
                                        />
                                      )}
                                    </span>
                                    {(node.memberId || node.stateEU?.code) && (
                                      <>
                                        {node.memberId && <p className='m-0'><FormattedMessage id='commissions.logs.row.memberId' defaultMessage='Member ID: {id}' values={{id: node.memberId}} /></p>}
                                        {node.stateEU?.code && <p className='m-0'><FormattedMessage id='commissions.logs.row.state' defaultMessage='State: {code}' values={{code: node.stateEU?.code}} /></p>}
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div>
                                    <span className='text-primary'>
                                      {node.user.firstName} {node.user.lastName}
                                      <p style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                        <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.user.npn}} />
                                      </p>
                                    </span>
                                  </div>
                                </td>
                                {!isAgente && (
                                  <td>
                                    {node.assignedTo ? (
                                      <div>
                                        <span>
                                          {node.assignedTo.firstName} {node.assignedTo.lastName}
                                        </span>
                                        {node.assignedTo.npn && (
                                          <p className='m-0' style={{maxWidth: '140px', wordBreak: 'break-all'}}>
                                            <FormattedMessage id='commissions.detail.npnValue' defaultMessage='NPN: {npn}' values={{npn: node.assignedTo.npn}} />
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <span className='text-muted'>{emDash}</span>
                                    )}
                                  </td>
                                )}
                                <td>
                                  <div>
                                    <span
                                      className={`commission-type ${node.commissionType.toLowerCase()}`}
                                      style={{maxWidth: '140px', wordBreak: 'break-all'}}
                                    >
                                      {commissionTypeLabel(node.commissionType)}
                                    </span>
                                    {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear) && (
                                      <p className='m-0'>
                                        <FormattedMessage id='commissions.detail.monthLabel' defaultMessage='Month:' /> {formatEffectivePeriod(node.effectiveMonth, node.effectiveYear)}
                                      </p>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <BadgeStatus state={node.status} />
                                </td>
                                <td>{parsePrice(node.rate)}</td>
                                <td>{node.members}</td>
                                <td>{parsePrice(node.subtotal)}</td>
                                <td
                                  className='d-flex gap-2 align-items-center'
                                  style={{minWidth: '120px'}}
                                >
                                  {report?.status === 'COMPLETED' && (
                                    <Button
                                      size='sm'
                                      variant='primary'
                                      onClick={() => {
                                        setIsOpenNotesClaims(true)
                                        setInfoEditDetail(node)
                                      }}
                                    >
                                      <i className='bi bi-chat-dots' /> <FormattedMessage id='commissions.detail.notesClaims' defaultMessage='Notes | Claims' />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </>
                        )
                      })()}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>
      {canUseCommissionsFinder && (
        <>
          <Button
            style={floatingTabStyle}
            variant='light'
            title={intl.formatMessage({id: 'commissions.logs.finder', defaultMessage: 'Commissions Finder'})}
            onClick={onOpenSearchDetails}
          >
            <i className='bi bi-search fs-4' />
          </Button>

          <OffcanvasSearchCommissionDetails
            isOpen={isOpenSearchDetails}
            onClose={onCloseSearchDetails}
          />
        </>
      )}

      {/*
      showAside && (
        <AsideMenucommissionReports
          isOpen={showAside}
          onClose={() => setShowAside(false)}
          tabFilters={tabFilters}
          setTabFilters={setTabFilters}
        />
      )
      */}

      {isOpen && (
        <ModalAddCommissionReportDetail
          refetch={refetchEntries}
          isOpen={isOpen}
          onClose={onClose}
          commissionReportId={report.id}
          defaultEffectiveMonth={report.effectiveMonth}
          defaultEffectiveYear={report.effectiveYear}
        />
      )}

      {isOpenEdit && infoEdit && (
        <ModalEditCommissionReport
          isOpen={isOpenEdit}
          onClose={() => {
            setIsOpenEdit(false)
          }}
          infoEdit={infoEdit}
          refetch={refetchReport}
        />
      )}

      {isOpenEditDetail && (
        <ModalEditCommissionReportDetail
          isOpen={isOpenEditDetail}
          onClose={() => {
            setIsOpenEditDetail(false)
          }}
          infoEdit={infoEditDetail}
          refetch={refetchEntries}
        />
      )}

      {isOpenAssignAgent && (
        <ModalAssignAgent
          isOpen={isOpenAssignAgent}
          onClose={() => setIsOpenAssignAgent(false)}
          infoEdit={infoEditDetail}
          refetch={refetchEntries}
        />
      )}
      {isOpenDeleteCommissionReportDetail && infoEditDetail && (
        <ModalDeleteCommissionReportDetail
          isOpen={isOpenDeleteCommissionReportDetail}
          onClose={() => {
            onCloseDeleteCommissionReportDetail()
          }}
          infoEdit={{id: infoEditDetail.id}}
          refetch={refetchEntries}
        />
      )}
      {isOpenNotesClaims && (
        <ModalNotesClaims
          isOpen={isOpenNotesClaims}
          onClose={() => {
            setIsOpenNotesClaims(false)
          }}
          infoEdit={infoEditDetail}
          refetch={refetchEntries}
        />
      )}
      {isOpenToggleModal && (
        <ModalConfirmation
          isOpen={isOpenToggleModal}
          onClose={() => setIsOpenToggleModal(false)}
          onConfirm={handleToggleStatus}
          refetch={refetchEntries}
          title={toggleAction === 'close'
            ? intl.formatMessage({id: 'commissions.reports.close.title', defaultMessage: 'Close Report'})
            : intl.formatMessage({id: 'commissions.reports.reopen.title', defaultMessage: 'Reopen Report'})}
          confirmText={toggleAction === 'close'
            ? intl.formatMessage({id: 'commissions.reports.close.confirm', defaultMessage: 'Yes, Close Report'})
            : intl.formatMessage({id: 'commissions.reports.reopen.confirm', defaultMessage: 'Yes, Reopen Report'})}
          confirmVariant={toggleAction === 'close' ? 'danger' : 'success'}
          body={
            toggleAction === 'close' ? (
              <>
                <p>
                  <strong><FormattedMessage id='commissions.reports.close.q' defaultMessage='Are you sure you wish to close this report?' /></strong>
                </p>
                <ul>
                  <li>
                    <FormattedMessage
                      id='commissions.detail.payroll.closeLi1'
                      defaultMessage='All <b>PENDING</b> policies will be marked as <b>NOT ASSIGNED</b>.'
                      values={{b: (chunks: React.ReactNode) => <strong>{chunks}</strong>}}
                    />
                  </li>
                  <li><FormattedMessage id='commissions.detail.payroll.closeLi2' defaultMessage='You confirm the rates and assignments are final.' /></li>
                </ul>
              </>
            ) : (
              <p>
                <FormattedMessage
                  id='commissions.detail.payroll.reopenBody'
                  defaultMessage='<b>Are you sure you want to reopen this report?</b><br></br>All <b>NOT ASSIGNED</b> policies will be marked as <b>PENDING</b>.'
                  values={{
                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                    br: () => <br />,
                  }}
                />
              </p>
            )
          }
        />
      )}
      {isOpenDeductionsModal && (
        <ModalConfirmation
          isOpen={isOpenDeductionsModal}
          refetch={refetchEntries}
          onClose={() => setIsOpenDeductionsModal(false)}
          onConfirm={handleConfirmCalculateDeductions}
          title={intl.formatMessage({id: 'commissions.detail.calculateDeductions', defaultMessage: 'Calculate Deductions'})}
          confirmText={intl.formatMessage({id: 'commissions.detail.deductions.confirm', defaultMessage: 'Yes, Calculate'})}
          confirmVariant='primary'
          body={
            <p>
              <FormattedMessage
                id='commissions.detail.deductions.body'
                defaultMessage="<b>Are you sure you want to calculate deductions?</b><br></br>This will run the deduction calculation logic and may update the report's values."
                values={{
                  b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                  br: () => <br />,
                }}
              />
            </p>
          }
        />
      )}

      {/* Modal para seleccionar tipo de upload */}
      {isOpenUploadTypeModal && (
        <ModalConfirmation
          isOpen={isOpenUploadTypeModal}
          refetch={() => {}}
          onClose={() => {
            setIsOpenUploadTypeModal(false)
            pendingFileRef.current = null
          }}
          onConfirm={handleConfirmUpload}
          title={intl.formatMessage({id: 'commissions.detail.upload.typeTitle', defaultMessage: 'Select Upload Type'})}
          confirmText={
            uploadType === 'commission' ? (
              <>
                <i className='bi bi-cash-stack me-2'></i>
                <FormattedMessage id='commissions.detail.upload.asCommission' defaultMessage='Upload as Commission' />
              </>
            ) : (
              <>
                <i className='bi bi-gift me-2'></i>
                <FormattedMessage id='commissions.detail.upload.asBonus' defaultMessage='Upload as Bonus' />
              </>
            )
          }
          confirmVariant='primary'
          body={
            <div>
              <p>
                <strong><FormattedMessage id='commissions.detail.upload.question' defaultMessage='What type of payment are you uploading?' /></strong>
              </p>
              <Form.Group className='mt-3'>
                <Form.Check
                  type='radio'
                  id='upload-commission'
                  label={intl.formatMessage({id: 'commissions.detail.upload.commissionPayment', defaultMessage: 'Commission Payment'})}
                  checked={uploadType === 'commission'}
                  onChange={() => setUploadType('commission')}
                />
                <Form.Text className='text-muted ms-4'>
                  <FormattedMessage id='commissions.detail.upload.commissionHint' defaultMessage='Upload regular commission payments for agents' />
                </Form.Text>
              </Form.Group>
              <Form.Group className='mt-2'>
                <Form.Check
                  type='radio'
                  id='upload-bonus'
                  label={intl.formatMessage({id: 'commissions.detail.upload.bonusPayment', defaultMessage: 'Bonus Payment'})}
                  checked={uploadType === 'bonus'}
                  onChange={() => setUploadType('bonus')}
                />
                <Form.Text className='text-muted ms-4'>
                  <FormattedMessage id='commissions.detail.upload.bonusHint' defaultMessage='Upload bonus payments. All approved records will be marked as BONUS type.' />
                </Form.Text>
              </Form.Group>
            </div>
          }
        />
      )}
    </div>
  )
}
